/**
 * Reusable Menu Rendering Components
 *
 * Components for rendering different types of menu items with role-based access
 */

import * as Icons from "@ant-design/icons";
import { Menu } from "antd";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import IntlMessages from "utils/IntlMessages";

const { SubMenu, Item: MenuItem, ItemGroup: MenuItemGroup } = Menu;

/**
 * Render a single menu item
 */
export const MenuItemComponent = ({ item, badge = null, ...props }) => {
  const Icon = item.icon && Icons[item.icon];

  return (
    <MenuItem key={item.key} {...props}>
      <Link to={item.path}>
        {Icon && <Icon className="icon" />}
        <span>
          {item.titleId ? <IntlMessages id={item.titleId} /> : item.title}
          {badge}
        </span>
      </Link>
    </MenuItem>
  );
};

MenuItemComponent.propTypes = {
  item: PropTypes.shape({
    key: PropTypes.string,
    path: PropTypes.string,
    icon: PropTypes.string,
    titleId: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
  badge: PropTypes.node,
};

/**
 * Render a submenu with nested items
 */
export const SubMenuComponent = ({
  submenu,
  navStyle,
  badge = null,
  children,
  ...props
}) => {
  const Icon = submenu.icon && Icons[submenu.icon];

  return (
    <SubMenu
      key={submenu.key}
      popupClassName={
        navStyle === "NAV_STYLE_NO_HEADER_MINI_SIDEBAR"
          ? "gx-no-header-submenu-popup"
          : ""
      }
      title={
        <span className="items-center">
          {Icon && <Icon className="icon" />}
          <span>
            {submenu.titleId ? (
              <IntlMessages id={submenu.titleId} />
            ) : (
              submenu.title
            )}
            {badge}
          </span>
        </span>
      }
      {...props}
    >
      {children ||
        submenu.items?.map((item) => {
          return <MenuItemComponent key={item.key} item={item} />;
        })}
    </SubMenu>
  );
};

SubMenuComponent.propTypes = {
  submenu: PropTypes.shape({
    key: PropTypes.string,
    titleId: PropTypes.string,
    title: PropTypes.string,
    icon: PropTypes.string,
    items: PropTypes.array,
  }).isRequired,
  navStyle: PropTypes.string,
  badge: PropTypes.node,
  children: PropTypes.node,
};

/**
 * Render a menu group
 */
export const MenuGroupComponent = ({ group, facilityType, children }) => {
  const filteredItems = group.items?.filter((item) => {
    if (!item.facilityTypes) return true;
    return item.facilityTypes.includes(facilityType);
  });

  return (
    <MenuItemGroup
      key={group.key}
      className="gx-menu-group"
      title={group.titleId ? <IntlMessages id={group.titleId} /> : group.title}
    >
      {children ||
        filteredItems?.map((item) => {
          if (item.type === "submenu") {
            return <SubMenuComponent key={item.key} submenu={item} />;
          }
          return <MenuItemComponent key={item.key} item={item} />;
        })}
    </MenuItemGroup>
  );
};

MenuGroupComponent.propTypes = {
  group: PropTypes.shape({
    key: PropTypes.string,
    titleId: PropTypes.string,
    title: PropTypes.string,
    items: PropTypes.array,
  }).isRequired,
  facilityType: PropTypes.string,
  children: PropTypes.node,
};

/**
 * Render admin menus (global or facility)
 */
export const AdminMenuSection = ({ config, facilityType = null, navStyle }) => {
  return (
    <MenuGroupComponent group={config} facilityType={facilityType}>
      {config.items?.map((item) => {
        if (item.type === "submenu") {
          return (
            <SubMenuComponent
              key={item.key}
              submenu={item}
              navStyle={navStyle}
            />
          );
        }

        return <MenuItemComponent key={item.key} item={item} />;
      })}
    </MenuGroupComponent>
  );
};

AdminMenuSection.propTypes = {
  config: PropTypes.shape({
    key: PropTypes.string,
    title: PropTypes.string,
    titleId: PropTypes.string,
    items: PropTypes.array,
  }).isRequired,
  facilityType: PropTypes.string,
  navStyle: PropTypes.string,
};

/**
 * Render universal access menus (File Management, Non-Conformity)
 */
export const UniversalMenuSection = ({ config }) => {
  // Guard against invalid config on initial load
  if (!config || !config.items || config.items.length === 0) {
    return null;
  }

  return (
    <MenuGroupComponent group={config}>
      {config.items.map((item) => {
        // Skip items without required data
        if (
          !item ||
          (!item.key && !item.path && !item.title && !item.titleId)
        ) {
          return null;
        }
        return <MenuItemComponent key={item.key} item={item} />;
      })}
    </MenuGroupComponent>
  );
};

UniversalMenuSection.propTypes = {
  config: PropTypes.shape({
    key: PropTypes.string,
    title: PropTypes.string,
    titleId: PropTypes.string,
    items: PropTypes.array,
  }).isRequired,
};
